import React, { useEffect, useState } from "react";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { styled } from "@mui/material/styles";
import TableRow from "@mui/material/TableRow";
import {
  Button,
  Card,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
} from "@mui/material";
import { TimeSheet } from "src/app/interfaces/timesheet";
import { adminTimeSheetService } from "src/app/services/api/admin-timesheet.service";
import moment from "moment";
import TimeSheetFilter from "./TimeSheetFilters";
import { blueGrey } from "@mui/material/colors";
import { showMessage } from "app/store/fuse/messageSlice";
import { useDispatch } from "react-redux";

const ColorButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.common.black,
  color: theme.palette.common.white,
  "&:hover": {
    backgroundColor: blueGrey[900],
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#202441",
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const WorkLogDetails: React.FC = () => {
  const [originalWorkLogs, setOriginalWorkLogs] = useState<TimeSheet[]>([]);
  const [filteredWorkLogs, setFilteredWorkLogs] = useState<TimeSheet[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selected, setSelected] = useState<readonly number[]>([]);
  const [isBatchGenerated, setIsBatchGenerated] = useState<boolean>(false);

  const dispatch = useDispatch();

  useEffect(() => {
    fetchWorkLogs();
  }, []);

  const fetchWorkLogs = (): void => {
    adminTimeSheetService
      .getAll()
      .then((res) => {
        setOriginalWorkLogs(res.data);
        setFilteredWorkLogs(res.data);
      })
      .catch((err) => console.error("Failed to load work logs", err.message));
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = paginatedData.map((n) => n.id);
      setSelected(newSelected);
    } else {
      setSelected([]);
    }
  };

  const handleClick = (id: number) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: readonly number[] = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter((selectedId) => selectedId !== id);
    }

    setSelected(newSelected);
  };

  const isSelected = (id: number) => selected.includes(id);

  const parseWeekRange = (week: string) => {
    const dateRangeRegex = /\((\d{2}-\d{2}-\d{4}) - (\d{2}-\d{2}-\d{4})\)/;
    const match = week.match(dateRangeRegex);
    if (!match) return null;
    return {
      start: moment(match[1], "MM-DD-YYYY"),
      end: moment(match[2], "MM-DD-YYYY"),
    };
  };

  const onSearch = (filters: {
    program?: string;
    week?: string;
    worksite?: string;
    status?: string;
    batchId?: string;
  }) => {
    let filtered = [...originalWorkLogs];

    if (filters.week) {
      const range = parseWeekRange(filters.week);
      if (range) {
        filtered = filtered.filter((log) =>
          moment(log.date).isBetween(range.start, range.end, undefined, "[]")
        );
      }
    }

    if (filters.status) {
      filtered = filtered.filter(
        (log) => log.status.toLowerCase() === filters.status?.toLowerCase()
      );
    }

    if (filters.batchId) {
      filtered = filtered.filter((log) => log.batchId === filters.batchId);
    }

    if (filters.program) {
      filtered = filtered.filter((log) => log.program === filters.program);
    }

    setFilteredWorkLogs(filtered);
    setPage(0);
  };

  const paginatedData = filteredWorkLogs.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const onGenerateBatch = () => {
    if (isBatchGenerated) {
      dispatch(
        showMessage({
          message: "Batch Already generated.",
          variant: "warning",
        })
      );
      return;
    }

    const updatedLogs = filteredWorkLogs.map((log) => {
      if (!log.batchId) {
        return {
          ...log,
          batchId: "BAT006",
          status: "Batched",
        };
      }
      return log;
    });

    setFilteredWorkLogs(updatedLogs as TimeSheet[]);

    // Also update original data to persist it for future filters
    const updatedOriginalLogs = originalWorkLogs.map((log) => {
      if (!log.batchId) {
        return {
          ...log,
          batchId: "BAT006",
          status: "Batched",
        };
      }
      return log;
    });

    setOriginalWorkLogs(updatedOriginalLogs as TimeSheet[]);

    setIsBatchGenerated(true);

    dispatch(
      showMessage({
        message: "Batch Generated Successfully. Batch ID: BAT006",
        variant: "success",
      })
    );
  };

  const onSubmitADP = () => {
    dispatch(
      showMessage({
        message: "Batch Details successfully submitted to ADP",
        variant: "success",
      })
    );
  };

  return (
    <>
      <TimeSheetFilter onFilterChange={(filters) => onSearch(filters)} />
      <div className="m-24">
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 700 }} aria-label="work log table">
            <TableHead>
              <StyledTableRow>
                <StyledTableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selected.length > 0 &&
                      selected.length < paginatedData.length
                    }
                    checked={
                      paginatedData.length > 0 &&
                      selected.length === paginatedData.length
                    }
                    onChange={handleSelectAllClick}
                    inputProps={{ "aria-label": "select all rows" }}
                  />
                </StyledTableCell>
                <StyledTableCell>Batch ID</StyledTableCell>
                <StyledTableCell>Program</StyledTableCell>
                <StyledTableCell>Host</StyledTableCell>
                <StyledTableCell>Date</StyledTableCell>
                <StyledTableCell>Youth</StyledTableCell>
                <StyledTableCell>Description</StyledTableCell>
                <StyledTableCell>Hours</StyledTableCell>
                <StyledTableCell>Rate</StyledTableCell>
                <StyledTableCell>Total</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((log) => {
                const isItemSelected = isSelected(log.id);
                return (
                  <StyledTableRow
                    key={log.id}
                    hover
                    aria-checked={isItemSelected}
                    selected={isItemSelected}
                    onClick={() => handleClick(log.id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <StyledTableCell padding="checkbox">
                      <Checkbox checked={isItemSelected} />
                    </StyledTableCell>
                    <StyledTableCell>{log.batchId ?? "-"}</StyledTableCell>
                    <StyledTableCell>{log.program}</StyledTableCell>
                    <StyledTableCell>{log.host}</StyledTableCell>
                    <StyledTableCell>
                      {moment(log.date).format("MM/DD/yyyy")}
                    </StyledTableCell>
                    <StyledTableCell>{log.youthName}</StyledTableCell>
                    <StyledTableCell>{log.workDescription}</StyledTableCell>
                    <StyledTableCell>{log.hoursWorked}</StyledTableCell>
                    <StyledTableCell>
                      {log.hourlyRate.toFixed(2)}
                    </StyledTableCell>
                    <StyledTableCell>
                      {(log.hourlyRate * log.hoursWorked).toFixed(2)}
                    </StyledTableCell>
                    <StyledTableCell>{log.status}</StyledTableCell>
                  </StyledTableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredWorkLogs.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </div>
      <Card variant="outlined" className="mb-12 ml-24 mr-24">
        <div className="flex flex-row gap-16 mb-24 mt-24">
          <ColorButton
            className="mx-8"
            variant="contained"
            color="primary"
            disabled={false}
            onClick={onGenerateBatch}
          >
            {"Generate a Batch"}
          </ColorButton>
          <ColorButton
            className="mx-8"
            variant="contained"
            color="primary"
            disabled={false}
            onClick={onSubmitADP}
          >
            {"Submit to ADP"}
          </ColorButton>
        </div>
      </Card>
    </>
  );
};

export default WorkLogDetails;
